import type { Pointer } from '@phi/jcore/module';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { Vector3 } from 'three';
import { isFace, isEdge, isVertex } from './lattice';
import App from './application-container';
import { Edge, Face, Vertex } from '@phi/jcore';
import { uil_is_object_visible } from './ui_logic';
import {
    display_add_STL_face_triangles,
    display_add_edge,
    display_add_face,
    display_add_vertex,
    display_clear_model,
    display_delete_object,
    display_modify_edge,
    display_modify_face,
    display_modify_vertex,
    display_set_face_curvatures,
    display_set_face_normals,
    uid_add_point_to_scene,
    uid_add_points_polyline_to_scene,
    uid_add_vector_to_scene,
} from './ui_display';
import { rgbToHex, uid_highlight_object } from './colours_materials_appearance';
import { console_log, free_array } from './utility';
import { Uid_face_triangle } from './graph_objects';

const DISABLE_CALLBACKS = false;

const log_level_texts: Record<LOG, string> = {
    [LOG.FATAL]: 'FATAL ERROR',
    [LOG.ERROR]: 'ERROR',
    [LOG.WARN]: 'WARNING',
    [LOG.INFO]: 'INFO',
    [LOG.DEBUG]: 'DEBUG MSG',
    [LOG.OTHER]: 'OTHER MSG',
};

export function display_log_message(level: LOG, msg: Pointer) {
    display_message_to_user(level, Module.UTF8ToString(msg));
}

export function modify_point(id: Vertex, x: number, y: number, z: number) {
    if (DISABLE_CALLBACKS) return; // XXX
    //console_log('modify_point:', id);
    display_modify_vertex(id, x, y, z);

    core_modified_objects_hash.setItem(id, uid_vertices.getItem(id));

    if ((App.state.dragging_type == 'vertex') && uil_is_object_visible(id)) {
        if (uid_drag_object)
            uid_highlight_object(uid_drag_object.object3d);
        uid_additional_drag_vertices.forEach((vid) => {
            uid_highlight_object(uid_vertices.getItem(vid)!.object3d!);
        });
    }
}

export function modify_polyline(id: Edge, n_points: number, newpoints_array: number) {
    if (DISABLE_CALLBACKS) return; // XXX

    //console_log('modify_polyline:', id);
    // Receives a 'pointer to an array of floats, of size n_elements'. It packages the array into a JS array 'newpoints' and calls the relevant
    // function that will update the display data structure.
    const newpointsF32A = new Float32Array(n_points * 3);

    // Read received data into Float32Array
    for (let i = 0; i < newpointsF32A.length; i++)
        newpointsF32A[i] = Module.getValue(
            newpoints_array + i * newpointsF32A.BYTES_PER_ELEMENT,
            'float'
        );

    //Turn into regular JS Array
    const newpoints = Array.prototype.slice.call(newpointsF32A);

    const point_vectors: Vector3[] = [];
    for (let i = 0; i < n_points; i++)
        point_vectors.push(new Vector3(
            newpoints[i * 3],
            newpoints[i * 3 + 1],
            newpoints[i * 3 + 2],
        ));

    core_modified_objects_hash.setItem(id, uid_edges.getItem(id));

    //uid_highlight_object(uid_edges.getItem(id).object3d);
    display_modify_edge(id, point_vectors);
    if ((App.state.dragging_type == 'vertex' || App.state.dragging_type == 'edge') && uil_is_object_visible(id))
        uid_highlight_object(uid_edges.getItem(id)!.object3d!);

    //diagnose_edge(id);
}

export function modify_face(id: Face, n_points: number, new_points_array: number) {
    if (DISABLE_CALLBACKS) return; // XXX

    //console_log('modify_face:', id);
    // Receives a 'pointer to an array of floats', of size n_points*3. It packages the arrays into a JS array 'newpoints' and calls the
    // relevant function that will update the display data structure.
    const newpointsF32A = new Float32Array(n_points * 3);

    // Read received data into Float32Array
    for (let i = 0; i < newpointsF32A.length; i++)
        newpointsF32A[i] = Module.getValue(
            new_points_array + i * newpointsF32A.BYTES_PER_ELEMENT,
            'float'
        );

    //Turn into regular JS Array
    const newpoints = Array.prototype.slice.call(newpointsF32A);

    core_modified_objects_hash.setItem(id, uid_faces.getItem(id));

    display_modify_face(id, newpoints);
}

export function add_point(id: Vertex, x: number, y: number, z: number) {
    if (DISABLE_CALLBACKS) return; // XXX

    if (uid_vertices.hasItem(id)) {
        const str = uil_is_object_visible(id) ? 'visible' : 'invisible';
        console_log('Trying to add existing vertex ' + id + '. Updating instead');
        return modify_point(id, x, y, z);
    }

    /*
    let str = uil_is_object_visible(id) ? 'visible' : 'invisible';
    console.log('add_point (' + id + ') is ' + str);
    */

    display_add_vertex(id, x, y, z);
}

export function add_polyline(id: Edge, n_points: number, points_array: number) {
    if (DISABLE_CALLBACKS) return; // XXX

    if (uid_edges.hasItem(id)) {
        const str = uil_is_object_visible(id) ? 'visible' : 'invisible';
        console_log('Trying to add existing edge ' + id + '. Updating instead');
        return modify_polyline(id, n_points, points_array);
    }

    /*
    let str = uil_is_object_visible(id) ? 'visible' : 'invisible';
    console.log('add_polyline (' + id + ') is ' + str);
    */

    // Receives a 'pointer to an array of floats, of size n_elements'. It packages the array into a JS array 'newpoints' and calls the relevant
    // function that will update the display data structure.

    const pointsF32A = new Float32Array(n_points * 3);

    // Read received data into Float32Array
    for (let i = 0; i < pointsF32A.length; i++)
        pointsF32A[i] = Module.getValue(
            points_array + i * pointsF32A.BYTES_PER_ELEMENT,
            'float'
        );

    //Turn into regular JS Array
    const points = Array.prototype.slice.call(pointsF32A);

    const point_vectors = [];
    for (let i = 0; i < n_points; i++) {
        point_vectors.push(
            new Vector3(points[i * 3], points[i * 3 + 1], points[i * 3 + 2])
        );
    }

    free_array(points);

    if (uid_current_api_call == 'phi_add_vertex') {
        // Mark the new edge id for the phi_add_vertex call
        if (uid_api_add_vertex_new_edge1_id == 0)
            uid_api_add_vertex_new_edge1_id = id;
        else if (uid_api_add_vertex_new_edge2_id == 0)
            uid_api_add_vertex_new_edge2_id = id;
    } else if (uid_current_api_call == 'phi_delete_vertex')
        uid_api_delete_vertex_new_edge_id = id;

    display_add_edge(id, point_vectors);
    free_array(point_vectors);
}

export function add_face(id: Face, n_points: number, coords_array: number, n_triangles: number, triangle_ids_array: number) {
    if (DISABLE_CALLBACKS) return; // XXX

    if (uid_faces.hasItem(id)) {
        const str = uil_is_object_visible(id) ? 'visible' : 'invisible';
        console_log('Trying to add existing face ' + id + '. Updating instead');
        return modify_face(id, n_points, coords_array);
    }
    /*
    let str = uil_is_object_visible(id) ? 'visible' : 'invisible';
    console.log('add_face (' + id + ') is ' + str);
    */

    // Receives a 'pointer to an array of floats', of size n_points*3, and a 'pointer to an array of integers' of size n_triangles*3.
    // It packages the arrays into a JS array 'newpoints' and calls the
    // relevant function that will update the display data structure.

    const pointsF32A = new Float32Array(n_points * 3);
    const trianglesI32A = new Int32Array(n_triangles * 3);

    // Read received data into Float32Array
    for (let i = 0; i < pointsF32A.length; i++)
        pointsF32A[i] = Module.getValue(
            coords_array + i * pointsF32A.BYTES_PER_ELEMENT,
            'float'
        );

    //Turn into regular JS Array
    const points = Array.prototype.slice.call(pointsF32A);
    // Make typed array candidate for garbage collection.

    // Read received data into Int32Array
    for (let i = 0; i < trianglesI32A.length; i++)
        trianglesI32A[i] = Module.getValue(
            triangle_ids_array + i * trianglesI32A.BYTES_PER_ELEMENT,
            'i32'
        );

    //Turn into regular JS Array
    const triangle_ids = Array.prototype.slice.call(trianglesI32A);
    // Make typed array candidate for garbage collection.

    const point_vectors: Vector3[] = [];
    for (let i = 0; i < n_points; i++) {
        point_vectors.push(
            new Vector3(points[i * 3], points[i * 3 + 1], points[i * 3 + 2])
        );
    }
    free_array(points);

    const triangles: Uid_face_triangle[] = [];
    for (let i = 0; i < n_triangles; i++) {
        triangles.push(
            new Uid_face_triangle(
                triangle_ids[i * 3]!,
                triangle_ids[i * 3 + 1]!,
                triangle_ids[i * 3 + 2]!,
            )
        );
    }

    free_array(triangle_ids);

    if (uid_current_api_call == 'phi_add_edge') {
        // Mark the new face id for the phi_add_edge call
        if (uid_api_add_edge_new_face1_id == 0)
            uid_api_add_edge_new_face1_id = id;
        else if (uid_api_add_edge_new_face2_id == 0)
            uid_api_add_edge_new_face2_id = id;
        //else
        //console_log('Error marking new face id while adding edge');
    }

    if (uid_current_api_call == 'phi_pop') {
        // Mark the new face id for the phi_pop call
        uid_api_pop_faces.push(id);
    }


    if (uid_current_api_call == 'phi_undo' || uid_current_api_call == 'phi_redo')
        display_add_face(id, point_vectors, triangles, null, null);
    else
        display_add_face(id, point_vectors, triangles, uid_callback_face_color, uid_callback_face_material);

    free_array(point_vectors);
    free_array(triangles);
}

export function delete_object(object_id: Face | Edge | Vertex) {
    if (DISABLE_CALLBACKS) return; // XXX

    /*
    let str = " (other)";
    if (isFace(object_id))
        str = " (face)";
    else if (isEdge(object_id))
        str = " (edge)";
    else if (isVertex(object_id))
        str = " (vertex)";
    console.log('delete_object: ' + object_id + str);
    */

    // Keep the deleted object material to use it when adding new object, hoping for the best
    // Unless we're loading a new model
    if (uid_current_api_call != 'phi_load_model') {
        if (isFace(object_id)) {
            uid_callback_face_material = uid_faces.getItem(object_id)!.material_name;
            const f = uid_faces.getItem(object_id);
            uid_callback_face_color = rgbToHex(f!.color_r, f!.color_g, f!.color_b);
            uid_face_colors.removeItem(object_id);
        }
    }

    // Remove from list of selected objects as well
    if (isFace(object_id) && uid_selected_faces.hasItem(object_id))
        uid_selected_faces.removeItem(object_id);
    if (isEdge(object_id) && uid_selected_edges.hasItem(object_id))
        uid_selected_edges.removeItem(object_id);
    if (isVertex(object_id) && uid_selected_vertices.hasItem(object_id))
        uid_selected_vertices.removeItem(object_id);

    display_delete_object(object_id);
}

export function clear_model() {
    if (DISABLE_CALLBACKS) return; // XXX
    display_clear_model();
}

/** Takes as input a triangulated face id, the
 * number n_points of points comprising its triangulation vertices and an
 * array [0..n_points-1] of the corresponding surface curvature values for
 * each such vertex.
 * It packages the arrays into a JS array 'curvatures' and calls the
 * relevant function that will update the display data structure.
 */
export function set_face_curvatures(id: Face, n_points: number, curvatures_array: number) {
    const curvaturesF32A = new Float32Array(n_points);

    // Read received data into Float32Array
    for (let i = 0; i < curvaturesF32A.length; i++)
        curvaturesF32A[i] = Module.getValue(
            curvatures_array + i * curvaturesF32A.BYTES_PER_ELEMENT,
            'float'
        );

    //Turn into regular JS Array
    const curvatures: number[] = Array.prototype.slice.call(curvaturesF32A);

    display_set_face_curvatures(id, curvatures);
}

/** Receives a 'pointer to an array of floats', of size n_points*3, and a 'pointer to an array of integers' of size n_triangles*3.
 * It packages the arrays into a JS array 'newpoints' and calls the
 * relevant function that will update the display data structure.
 */
export function set_STL_face_triangles(id: number, n_points: number, coords_array: number, n_triangles: number, triangle_ids_array: number) {
    const pointsF32A = new Float32Array(n_points * 3);
    const trianglesI32A = new Int32Array(n_triangles * 3);

    // Read received data into Float32Array
    for (let i = 0; i < pointsF32A.length; i++)
        pointsF32A[i] = Module.getValue(
            coords_array + i * pointsF32A.BYTES_PER_ELEMENT,
            'float'
        );
    //Turn into regular JS Array
    const points: number[] = Array.prototype.slice.call(pointsF32A);

    // Read received data into Int32Array
    for (let i = 0; i < trianglesI32A.length; i++)
        trianglesI32A[i] = Module.getValue(
            triangle_ids_array + i * trianglesI32A.BYTES_PER_ELEMENT,
            'i32'
        );

    //Turn into regular JS Array
    const triangle_ids: number[] = Array.prototype.slice.call(trianglesI32A);

    const point_vectors: Vector3[] = [];
    for (let i = 0; i < n_points; i++) {
        point_vectors.push(
            new Vector3(points[i * 3], points[i * 3 + 1], points[i * 3 + 2])
        );
    }

    const triangles: Uid_face_triangle[] = [];
    for (let i = 0; i < n_triangles; i++) {
        triangles.push(
            new Uid_face_triangle(
                triangle_ids[i * 3]!,
                triangle_ids[i * 3 + 1]!,
                triangle_ids[i * 3 + 2]!,
            )
        );
    }

    display_add_STL_face_triangles(id, point_vectors, triangles);
}

export function display_show_vector(bx: number, by: number, bz: number, dx: number, dy: number, dz: number, l: number, r: number, g: number, b: number) {
    // XXX CAREFUL! Disregarding 'l' and setting it to 1, since (dx, dy, dz) already has the correct length.
    uid_add_vector_to_scene(bx, by, bz, dx, dy, dz, 1.0, r, g, b);
}

export function display_show_point(px: number, py: number, pz: number, r: number, g: number, b: number) {
    uid_add_point_to_scene(px, py, pz, r, g, b);
}

export function display_show_points_polyline(np: number, pcoords: number, r: number, g: number, b: number) {
    const pointsF32A = new Float32Array(np*3);

    // Read received data into Float32Array
    for (let i = 0; i < pointsF32A.length; i++)
        pointsF32A[i] = Module.getValue(
            pcoords + i * pointsF32A.BYTES_PER_ELEMENT,
            'float'
        );

    //Turn into regular JS Array
    const points_flat: number[] = Array.prototype.slice.call(pointsF32A);

    //Turn from flat array to vector array
    const point_vectors: Vector3[] = [];
    for (let i = 0; i < np; i++) {
        point_vectors.push(
            new Vector3(points_flat[i * 3], points_flat[i * 3 + 1], points_flat[i * 3 + 2])
        );
    }

    uid_add_points_polyline_to_scene(point_vectors, r, g, b);
}

/** Takes as input a triangulated face id, the
 * number n_points of points comprising its triangulation vertices and an
 * array [0..n_points*3-1] of the corresponding surface normal vector components (x,y,z) for
 * each such vertex.
 * It packages the arrays into a JS array 'newpoints' and calls the
 * relevant function that will update the display data structure.
 */
export function set_face_normals(id: Face, n_points: number, normals_array: number) {
    const normalsF32A = new Float32Array(n_points * 3);

    // Read received data into Float32Array
    for (let i = 0; i < normalsF32A.length; i++)
        normalsF32A[i] = Module.getValue(
            normals_array + i * normalsF32A.BYTES_PER_ELEMENT,
            'float'
        );

    //Turn into regular JS Array
    const normals: number[] = Array.prototype.slice.call(normalsF32A);

    const normal_vectors: Vector3[] = [];
    for (let i = 0; i < n_points; i++) {
        normal_vectors.push(
            new Vector3(normals[i * 3], normals[i * 3 + 1], normals[i * 3 + 2])
        );
    }

    display_set_face_normals(id, normal_vectors);
}
